import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShoppingCart, CheckCircle, Truck, CreditCard, FileText, ChevronLeft, ChevronRight, Shield, Calculator, Clock, Package, Wrench, Box, Download, X, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getMachineBySlug } from '../services/productApi';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import FinancingModal from '../components/FinancingModal';
import PayPalInstallmentOption from '../components/PayPalInstallmentOption';
import AvailabilityBadge from '../components/AvailabilityBadge';
import CartAddedModal from '../components/CartAddedModal';
import PreorderModal from '../components/PreorderModal';
import { isInquiryOnly as checkInquiryOnly, inquiryUrl } from '../utils/inquiryRules';
import MixedCartWarningModal from '../components/MixedCartWarningModal';
import SmartImage from '../components/SmartImage';

// Produktbeschreibungen für jeden Bagger
const productDescriptions = {
  'mini-bagger-qw10': `Der Minibagger QW10 ist ein kompakter, leistungsstarker Kettenbagger für vielseitige Einsatzbereiche im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf engen Baustellen. Durch seine kompakten Abmessungen eignet er sich ideal für Arbeiten in schmalen Durchfahrten, Hinterhöfen, Einfahrten und im Innenbereich.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW10 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Angetrieben wird der QW10 von einem KD192F Dieselmotor mit 7,0 kW Nennleistung (Euro 5). Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw12': `Der Minibagger QW12 ist ein kompakter, leistungsstarker Kettenbagger für vielseitige Einsatzbereiche im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf engen Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW12 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Angetrieben wird der QW12 von einem KD192F Dieselmotor mit 7,0 kW Nennleistung (Euro 5). Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw12s': `Der Minibagger QW12S ist die erweiterte Version unseres QW12 mit zusätzlichen Funktionen für noch vielseitigere Einsatzmöglichkeiten.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW12S verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Angetrieben wird der QW12S von einem KD192F Dieselmotor mit 7,0 kW Nennleistung (Euro 5). Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw13': `Der Minibagger QW13 ist ein vielseitiger Kettenbagger für Einsätze im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf mittleren bis engen Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW13 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw15': `Der Minibagger QW15 ist ein leistungsstarker Kettenbagger für anspruchsvolle Einsätze im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf mittleren bis engen Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW15 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Angetrieben wird der QW15 von einem Kubota D722 Dieselmotor mit 10,2 kW Nennleistung (Euro 5). Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw18': `Der Minibagger QW18 ist unser leistungsstärkster Kettenbagger für professionelle Einsätze im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf verschiedenen Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW18 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Angetrieben wird der QW18 von einem Kubota D722 Dieselmotor mit 10,2 kW Nennleistung (Euro 5). Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw20': `Der Minibagger QW20 ist ein kraftvoller Kettenbagger für professionelle Einsätze im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf größeren Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW20 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
  'mini-bagger-qw22': `Der Minibagger QW22 ist unser leistungsstärkstes Modell für anspruchsvolle professionelle Einsätze im Garten- und Landschaftsbau, Tiefbau, bei Erdarbeiten sowie auf größeren Baustellen.

Alle unsere Minibagger-Modelle sind serienmäßig mit einer geschlossenen Fahrerkabine (Enclosed Cabin) ausgestattet. Diese bietet Schutz vor Witterung, Staub und Schmutz und ermöglicht komfortables Arbeiten bei jeder Jahreszeit.

Der QW22 verfügt über einen manuellen Schnellwechsler, der werksseitig montiert und betriebsbereit geliefert wird. Anbaugeräte können dadurch schnell und sicher gewechselt werden.

Ein weiteres wichtiges Merkmal ist der seitenschwenkbare Auslegerarm (Boom Swing), der präzises Arbeiten entlang von Mauern, Gräben und Fundamenten ermöglicht. Die Maschine ist mit einer feinfühligen Joystick-Steuerung ausgestattet und lässt sich sehr leicht und präzise bedienen.

Das Kettenfahrwerk besitzt Gummiketten (Rubber Tracks) und ein spurverstellbares Unterwagen-System (Retractable Tracks / Spurverbreiterung). Dadurch kann der Bagger für enge Durchfahrten schmal eingestellt und für maximale Standsicherheit auf der Baustelle verbreitert werden.

Alle Modelle werden komplett montiert, geprüft und betriebsbereit ausgeliefert.`,
};

// Erweiterte Technische Daten für Bagger (korrigiert nach PDF-Produktkatalog)
const technischeDatenBagger = {
  'mini-bagger-qw10': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'KD192F',
        'Nennleistung': '7,0 kW',
        'Maximale Drehzahl': '3.000 U/min',
        'Kraftstofftank': '5 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Luftkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,0 t',
        'Maschinengewicht': 'ca. 800 kg',
        'Gesamtlänge': '2.800 mm',
        'Gesamtbreite (eingefahren)': '800 mm',
        'Gesamtbreite (ausgefahren)': '980 mm',
        'Gesamthöhe': '2.200 mm',
        'Spurbreite': '800 - 980 mm (Retractable Tracks)',
        'Kettenbreite': '180 mm'
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {
        'Maximale Grabtiefe': '1.600 mm',
        'Maximale Reichweite': '3.000 mm',
        'Max. Grabungshöhe': '2.550 mm',
        'Max. Ausschütthöhe': '1.750 mm',
        'Min. Schwenkradius': '1.300 mm',
        'Fahrgeschwindigkeit': '2,2 km/h',
        'Schaufelvolumen': '0,025 m³'
      }
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 30 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw12': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'KD192F',
        'Nennleistung': '7,0 kW',
        'Maximale Drehzahl': '3.000 U/min',
        'Kraftstofftank': '5 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Luftkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,2 t',
        'Maschinengewicht': 'ca. 850 kg',
        'Gesamtlänge': '2.800 mm',
        'Gesamtbreite (eingefahren)': '800 mm',
        'Gesamtbreite (ausgefahren)': '980 mm',
        'Gesamthöhe': '2.200 mm',
        'Spurbreite': '800 - 980 mm (Retractable Tracks)',
        'Kettenbreite': '180 mm'
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {
        'Maximale Grabtiefe': '1.650 mm',
        'Maximale Reichweite': '3.050 mm',
        'Max. Grabungshöhe': '2.600 mm',
        'Max. Ausschütthöhe': '1.800 mm',
        'Min. Schwenkradius': '1.360 mm',
        'Fahrgeschwindigkeit': '2,2 km/h',
        'Schaufelvolumen': '0,025 m³'
      }
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 30 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw12s': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'KD192F',
        'Nennleistung': '7,0 kW',
        'Maximale Drehzahl': '3.000 U/min',
        'Kraftstofftank': '5 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Luftkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,2 t',
        'Maschinengewicht': 'ca. 850 kg',
        'Gesamtlänge': '2.800 mm',
        'Gesamtbreite (eingefahren)': '800 mm',
        'Gesamtbreite (ausgefahren)': '980 mm',
        'Gesamthöhe': '2.200 mm',
        'Spurbreite': '800 - 980 mm (Retractable Tracks)',
        'Kettenbreite': '180 mm'
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {
        'Maximale Grabtiefe': '1.650 mm',
        'Maximale Reichweite': '3.050 mm',
        'Max. Grabungshöhe': '2.600 mm',
        'Max. Ausschütthöhe': '1.800 mm',
        'Min. Schwenkradius': '1.360 mm',
        'Fahrgeschwindigkeit': '2,2 km/h',
        'Schaufelvolumen': '0,025 m³'
      }
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 30 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw13': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'KD192F',
        'Nennleistung': '7,0 kW',
        'Maximale Drehzahl': '3.000 U/min',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Luftkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,3 t',
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {}
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 30 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw15': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'Kubota D722',
        'Nennleistung': '10,2 kW',
        'Maximale Drehzahl': '2.200 U/min',
        'Kraftstofftank': '8 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Wasserkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,5 t',
        'Maschinengewicht': 'ca. 1.100 kg',
        'Gesamtlänge': '3.200 mm',
        'Gesamtbreite (eingefahren)': '850 mm',
        'Gesamtbreite (ausgefahren)': '1.030 mm',
        'Gesamthöhe': '2.200 mm',
        'Spurbreite': '850 - 1.030 mm (Retractable Tracks)',
        'Kettenbreite': '180 mm'
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {
        'Maximale Grabtiefe': '1.760 mm',
        'Maximale Reichweite': '3.130 mm',
        'Max. Grabungshöhe': '2.760 mm',
        'Max. Ausschütthöhe': '1.990 mm',
        'Min. Schwenkradius': '1.400 mm',
        'Fahrgeschwindigkeit': '2,4 km/h',
        'Schaufelvolumen': '0,025 m³'
      }
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 40 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw18': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'Kubota D722',
        'Nennleistung': '10,2 kW',
        'Maximale Drehzahl': '2.200 U/min',
        'Kraftstofftank': '8 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Wasserkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '1,8 t',
        'Maschinengewicht': 'ca. 1.500 kg',
        'Gesamtlänge': '3.320 mm',
        'Gesamtbreite (eingefahren)': '940 mm',
        'Gesamtbreite (ausgefahren)': '1.120 mm',
        'Gesamthöhe': '2.250 mm',
        'Spurbreite': '940 - 1.120 mm (Retractable Tracks)',
        'Kettenbreite': '180 mm'
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {
        'Maximale Grabtiefe': '2.040 mm',
        'Maximale Reichweite': '3.400 mm',
        'Max. Grabungshöhe': '3.040 mm',
        'Max. Ausschütthöhe': '2.085 mm',
        'Min. Schwenkradius': '1.500 mm',
        'Fahrgeschwindigkeit': '2,4 km/h',
        'Schaufelvolumen': '0,035 m³'
      }
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 40 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw20': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'Kubota D722',
        'Nennleistung': '10,2 kW',
        'Maximale Drehzahl': '2.200 U/min',
        'Kraftstofftank': '8 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Wasserkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '2,0 t',
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {}
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 40 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  },
  'mini-bagger-qw22': {
    motorLeistung: {
      title: 'Motor & Leistungsdaten',
      data: {
        'Motortyp': 'Dieselmotor',
        'Motormodell': 'Kubota D722',
        'Nennleistung': '10,2 kW',
        'Maximale Drehzahl': '2.200 U/min',
        'Kraftstofftank': '8 Liter',
        'Startsystem': 'Elektrostarter',
        'Kühlung': 'Wasserkühlung',
        'Abgasnorm': 'Euro 5'
      }
    },
    abmessungenGewicht: {
      title: 'Abmessungen & Gewicht',
      data: {
        'Leistungsklasse': '2,2 t',
      }
    },
    leistungsdaten: {
      title: 'Leistungsdaten',
      data: {}
    },
    besonderheiten: {
      title: 'Besonderheiten',
      data: [
        'Seitenschwenkbarer Ausleger (Boom Swing)',
        'Geschlossene Kabine (Enclosed Cabin)',
        'Manueller Schnellwechsler (werksseitig montiert)',
        'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
        'Gummiketten (Rubber Tracks)',
        'Joystick-Steuerung',
        'Grabschaufel 40 cm im Lieferumfang',
        'Komplett montiert und betriebsbereit geliefert'
      ]
    }
  }
};

// Serienausstattung für Bagger
const serienausstattungBagger = [
  'Geschlossene Kabine (Enclosed Cabin)',
  'Manueller Schnellwechsler (vormontiert)',
  'Seitlich schwenkbarer Ausleger (Boom Swing)',
  'Spurverstellbares Fahrwerk (Retractable Undercarriage)',
  'Gummiketten (Rubber Tracks)',
  'Joystick-Steuerung',
  'Grabschaufel 30/40 cm',
  'Dieselmotor',
  'Schwenkbarer Oberwagen',
  'LED-Arbeitsbeleuchtung',
  'Sicherheitsbügel und Not-Aus',
  'Einsatzbereit montiert',
];

// Lieferumfang für Bagger
const lieferumfangBagger = {
  'mini-bagger-qw10': [
    'Minibagger QW10 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 30 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw12': [
    'Minibagger QW12 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 30 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw12s': [
    'Minibagger QW12S komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 30 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw13': [
    'Minibagger QW13 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 30 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw15': [
    'Minibagger QW15 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 40 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw18': [
    'Minibagger QW18 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 40 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw20': [
    'Minibagger QW20 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 40 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
  'mini-bagger-qw22': [
    'Minibagger QW22 komplett montiert',
    'Geschlossene Kabine',
    'Manueller Schnellwechsler',
    'Grabschaufel 40 cm',
    'Bordwerkzeug',
    'Bedienungsanleitung',
    'CE-Dokumente',
  ],
};


const ProductDetail = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinancingModal, setShowFinancingModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showPreorderModal, setShowPreorderModal] = useState(false);
  const [preorderMode, setPreorderMode] = useState(false);
  const [showMixedCartWarning, setShowMixedCartWarning] = useState(false);
  const [mixedCartMessage, setMixedCartMessage] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Keyboard navigation for lightbox (must be before early returns)
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => prev > 0 ? prev - 1 : prev);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => prev + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const productData = await getMachineBySlug(slug);
        setProduct(productData || null);
      } catch (error) {
        console.error('Error loading product:', error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
    setCurrentImageIndex(0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Produkt...</p>
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Produkt nicht gefunden</h2>
          <p className="text-gray-600 mb-6">Das gesuchte Produkt konnte leider nicht gefunden werden.</p>
          <Link to="/bagger">
            <Button className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="mr-2" size={18} />
              Zurück zu allen Baggern
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Build gallery images: prefer gallery (uploaded), fallback to images (legacy), then main image
  const galleryImages = (product.gallery && product.gallery.length > 0)
    ? product.gallery
    : (product.images && product.images.length > 0)
      ? product.images
      : [];

  // Combine: main image first, then gallery (avoiding duplicates)
  const allImages = product.image
    ? [product.image, ...galleryImages.filter(img => img !== product.image)]
    : galleryImages.length > 0 ? galleryImages : [];

  const images = allImages.length > 0 ? allImages : [product.image];
  const stockQuantity = product.stockQuantity ?? 10;
  const isOutOfStock = stockQuantity === 0;
  const isInStock = !isOutOfStock;

  const isInquiryOnly = checkInquiryOnly({ slug: product.slug, category: product.category });
  
  const handleAddToCart = () => {
    if (isOutOfStock) {
      toast.error('Dieses Produkt ist derzeit nicht verfügbar.');
      return;
    }
    if (product.pricing.numericPrice === null) {
      toast.info('Bitte kontaktieren Sie uns für ein individuelles Angebot.');
      navigate('/kontakt');
      return;
    }
    
    const result = addToCart(product, 1, false); // Regular purchase
    if (result.success) {
      setShowCartModal(true);
    } else {
      // Show mixed cart warning modal instead of toast
      setMixedCartMessage(result.error);
      setShowMixedCartWarning(true);
    }
  };
  
  const handlePreorder = () => {
    if (isInquiryOnly) {
      navigate(inquiryUrl(product.name || product.slug));
      return;
    }

    if (product.pricing.numericPrice === null) {
      toast.info('Bitte kontaktieren Sie uns für ein individuelles Angebot.');
      navigate('/kontakt');
      return;
    }
    
    const result = addToCart(product, 1, true); // Preorder
    if (result.success) {
      setPreorderMode(true);
      setShowPreorderModal(true);
    } else {
      // Show mixed cart warning modal instead of toast
      setMixedCartMessage(result.error);
      setShowMixedCartWarning(true);
    }
  };
  
  // Calculate preorder prices for display
  const basePrice = product.pricing?.numericPrice || 0;
  const preorderPrice = basePrice * 0.90; // 10% discount
  const depositAmountNet = preorderPrice * 0.20; // 20% deposit (net)
  const depositAmountGross = depositAmountNet * 1.19; // Brutto inkl. 19% MwSt


  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const certificates = product.documents?.filter(doc => doc.type === 'certificate') || [];

  const getCategoryInfo = () => {
    const category = product?.category;
    switch (category) {
      case 'Mini-Bagger':
        return { name: 'Bagger', link: '/bagger', urlPrefix: '/bagger' };
      case 'Radlader':
        return { name: 'Radlader', link: '/radlader', urlPrefix: '/radlader' };
      case 'Gabelstapler':
        return { name: 'Gabelstapler', link: '/gabelstapler', urlPrefix: '/gabelstapler' };
      case 'Rüttelplatte':
        return { name: 'Rüttelplatten', link: '/ruettelplatten', urlPrefix: '/ruettelplatten' };
      case 'Stampfer':
        return { name: 'Stampfer', link: '/stampfer', urlPrefix: '/stampfer' };
      default:
        return { name: 'Bagger', link: '/bagger', urlPrefix: '/bagger' };
    }
  };

  const categoryInfo = getCategoryInfo();

  const getAvailabilityInfo = () => {
    if (isOutOfStock) {
      return { text: t('products.notAvailable'), color: 'text-red-600', bgColor: 'bg-red-50', dotColor: 'bg-red-500' };
    } else if (stockQuantity <= 2) {
      return { text: t('products.limitedStock', { count: stockQuantity }), color: 'text-orange-600', bgColor: 'bg-orange-50', dotColor: 'bg-orange-500' };
    }
    return { text: t('products.available'), color: 'text-green-600', bgColor: 'bg-green-50', dotColor: 'bg-green-500' };
  };

  const availability = getAvailabilityInfo();

  const getProductDescription = () => {
    if (product.category === 'Mini-Bagger' && productDescriptions[slug]) {
      return productDescriptions[slug];
    }
    return product.description;
  };

  const getLieferumfang = () => {
    if (product.category === 'Mini-Bagger' && lieferumfangBagger[slug]) {
      return lieferumfangBagger[slug];
    }
    return null;
  };

  const getTechnischeDaten = () => {
    if (product.category === 'Mini-Bagger' && technischeDatenBagger[slug]) {
      return technischeDatenBagger[slug];
    }
    return null;
  };

  const isBagger = product.category === 'Mini-Bagger';
  const techDaten = getTechnischeDaten();

  // Grabschaufel-Größe modellspezifisch: QW10-QW13 = 30 cm, QW15-QW22 = 40 cm
  const getGrabschaufelSize = () => {
    const small = ['mini-bagger-qw10', 'mini-bagger-qw12', 'mini-bagger-qw12s', 'mini-bagger-qw13'];
    return small.includes(slug) ? '30' : '40';
  };

  // Get CE certificate URL for download
  const getCertificateUrl = () => {
    if (certificates.length > 0) {
      return certificates[0].url;
    }
    return null;
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrev = () => setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  const lightboxNext = () => setLightboxIndex((prev) => (prev + 1) % images.length);

  // Clamp lightbox index to valid range
  const safeLightboxIndex = Math.min(lightboxIndex, images.length - 1);

  return (
    <div className="min-h-screen bg-white">
      {/* LIGHTBOX */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          {/* Close button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-50"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 text-white/70 text-sm font-medium z-50">
            {safeLightboxIndex + 1} / {images.length}
          </div>

          {/* Previous */}
          {images.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-50"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {/* Image */}
          <SmartImage 
            src={images[safeLightboxIndex]} 
            alt={`${product.name} - Bild ${safeLightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Next */}
          {images.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-50"
              data-testid="lightbox-next"
            >
              <ChevronRight size={32} />
            </button>
          )}

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    safeLightboxIndex === idx ? 'border-white shadow-lg' : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <SmartImage src={img} alt="" className="w-full h-full object-contain bg-white/10 p-0.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* BREADCRUMB */}
      <div className="border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <nav className="flex items-center space-x-2 text-sm py-4">
            <Link to="/" className="text-gray-500 hover:text-red-600 transition-colors">Startseite</Link>
            <span className="text-gray-400">/</span>
            <Link to={categoryInfo.link} className="text-gray-500 hover:text-red-600 transition-colors">{categoryInfo.name}</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* HERO SECTION - 2 Spalten Layout */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            
            {/* LINKE SPALTE - Produktbild + Galerie */}
            <div className="flex flex-col">
              {/* Hauptbild - vergrößert für Höhenausgleich */}
              <div 
                className="relative bg-gray-50 rounded-xl overflow-hidden mb-4 flex-grow cursor-pointer" 
                style={{ minHeight: '520px' }}
                onClick={() => openLightbox(currentImageIndex)}
              >
                <SmartImage
                  src={images[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                  style={{ minHeight: '520px' }}
                  data-testid="product-main-image"
                />
                
                {/* Einheitliches Verfügbarkeits-Badge */}
                <div className="absolute top-4 right-4">
                  <AvailabilityBadge stockQuantity={product.stockQuantity} inquiryOnly={isInquiryOnly} />
                </div>

                {/* Bildnavigation Pfeile */}
                {images.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage} 
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft size={24} className="text-gray-800" />
                    </button>
                    <button 
                      onClick={nextImage} 
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight size={24} className="text-gray-800" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail-Galerie */}
              {isBagger && images.length > 1 && (
                <div className={`grid gap-2 mt-auto ${
                  images.length <= 5 ? 'grid-cols-5' :
                  images.length <= 10 ? 'grid-cols-10' :
                  'grid-cols-10'
                }`}>
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index 
                          ? 'border-red-600 shadow-lg ring-2 ring-red-200' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <SmartImage 
                        src={img} 
                        alt={`${product.name} Bild ${index + 1}`} 
                        className="w-full h-full object-contain p-0.5 bg-white" 
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Standard Thumbnail-Galerie für andere Produkte */}
              {!isBagger && images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index 
                          ? 'border-red-600 shadow-lg' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <SmartImage src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-contain p-1 bg-white" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* RECHTE SPALTE - Produktinfos */}
            <div className="flex flex-col h-full">
              {/* 1. Kategorie mit Firmenname */}
              <p className="text-red-600 font-semibold text-sm uppercase tracking-wide mb-2">
                {product.category === 'Mini-Bagger' ? 'Minibagger' : product.category} – Jannesmann Baumaschinen
              </p>
              
              {/* 2. Modellname */}
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" data-testid="product-title">
                {product.name}
              </h1>

              {/* 3. Serienausstattung / Highlights Block - VOR dem Preis */}
              {isBagger && (
                <div className="bg-gray-100 rounded-xl p-5 mb-6 border-2 border-gray-300">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg border-b-2 border-red-600 pb-2">Serienausstattung / Highlights</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Geschlossene Kabine (Standard bei allen unseren Modellen)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Seitenschwenkbarer Auslegerarm</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Spurverstellbares Fahrwerk</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Manueller Schnellwechsler (werksseitig montiert)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Joystick-Steuerung</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Dieselmotor mit Elektrostarter</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Grabschaufel {getGrabschaufelSize()} cm im Lieferumfang</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-800 font-medium text-base">Komplett montiert und betriebsbereit geliefert</span>
                    </li>
                  </ul>
                </div>
              )}

              {/* 4. Lieferzeit, Verfügbarkeit, Preis, Vertrauenshinweise, Button */}
              <div className={`border-2 rounded-xl p-6 flex-grow flex flex-col ${isOutOfStock ? 'border-gray-300 bg-gray-50' : 'border-red-600 bg-white'}`}>
                {/* Lieferzeit & Verfügbarkeit zuerst */}
                <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center text-sm text-gray-700">
                    <Clock className="mr-2 text-gray-500" size={18} />
                    <span className="font-medium">{isOutOfStock ? 'Ca. 60 Tage Lieferzeit' : (product.deliveryTime || 'Lieferzeit auf Anfrage')}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Package className={`mr-2 ${availability.color}`} size={18} />
                    <span className={`font-medium ${availability.color}`}>{availability.text}</span>
                  </div>
                </div>

                {/* Verkaufspreis */}
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-gray-600 font-medium">{t('products.price')}</span>
                  <span className={`text-3xl lg:text-4xl font-bold ${isOutOfStock ? 'text-gray-400' : 'text-red-600'}`} data-testid="product-price">
                    {product.pricing.buy}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4">{t('products.vat')}</p>

                {/* Vertrauenshinweise - ÜBER dem Button */}
                <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-gray-200 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Truck className="text-red-600 mr-2" size={18} />
                    <span>{t('products.delivery')}</span>
                  </div>
                  <div className="flex items-center">
                    <CreditCard className="text-red-600 mr-2" size={18} />
                    <span>{t('products.securePayment')}</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="text-red-600 mr-2" size={18} />
                    <span>{t('products.ceCertified')}</span>
                  </div>
                </div>

                {/* Garantie-Kurzfassung */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-testid="warranty-info">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-green-800">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={16} />
                      <span className="font-medium">{t('warranty.title')}</span>
                    </div>
                    <div className="flex items-center text-sm text-green-800">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={16} />
                      <span className="font-medium">{t('warranty.parts')}</span>
                    </div>
                    <div className="flex items-center text-sm text-green-800">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={16} />
                      <span className="font-medium">{t('warranty.fast')}</span>
                    </div>
                  </div>
                  <Link to="/garantiebedingungen" className="text-xs text-green-700 hover:text-green-900 underline mt-2 inline-block">
                    {t('warranty.viewTerms')}
                  </Link>
                </div>

                {/* Out of Stock Warning */}
                {isOutOfStock && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="out-of-stock-notice">
                    <p className="text-amber-700 font-medium text-center">
                      {isInquiryOnly
                        ? 'Dieses Produkt ist aktuell nicht auf Lager – stellen Sie gerne eine unverbindliche Anfrage!'
                        : 'Dieses Produkt ist aktuell nicht auf Lager – Sie können es aber vorbestellen!'}
                    </p>
                  </div>
                )}

                {/* Spacer für gleiche Höhe */}
                <div className="flex-grow"></div>

                {/* Buttons Section */}
                <div className="space-y-3">
                  {/* In den Warenkorb Button - nur wenn auf Lager */}
                  {isInStock && product.pricing.numericPrice && (
                    <Button 
                      onClick={handleAddToCart} 
                      className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 text-white"
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="mr-2" size={22} />
                      {t('products.addToCart')}
                    </Button>
                  )}
                  
                  {/* Vorbestellen / Anfrage stellen Button */}
                  {product.pricing.numericPrice && (
                    <Button 
                      onClick={handlePreorder} 
                      className={`w-full py-6 text-lg text-white ${
                        isInquiryOnly
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : (isOutOfStock ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600')
                      }`}
                      data-testid={isInquiryOnly ? 'inquiry-btn' : 'preorder-btn'}
                    >
                      {isInquiryOnly ? (
                        <>
                          <Mail className="mr-2" size={22} />
                          Anfrage stellen
                        </>
                      ) : (
                        <>
                          <Package className="mr-2" size={22} />
                          Vorbestellen (–10%)
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Vorbestell-Info — nur bei echten Vorbestell-Modellen (QW12, QW15) anzeigen */}
                  {product.pricing.numericPrice && !isInquiryOnly && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                      <p className="text-sm text-amber-800">
                        <strong>Vorbestell-Vorteil:</strong> 10% Rabatt, nur 20% Anzahlung
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Anzahlung: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(depositAmountGross)} inkl. MwSt
                        {' '}(statt {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(basePrice * 1.19)} brutto)
                      </p>
                    </div>
                  )}
                  
                  {/* Angebot anfordern - wenn kein Preis */}
                  {!product.pricing.numericPrice && (
                    <Button 
                      onClick={() => navigate('/kontakt')} 
                      className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 text-white"
                      data-testid="request-quote-btn"
                    >
                      {t('products.requestQuote')}
                    </Button>
                  )}
                </div>

                {/* Finanzierungsbutton */}
                {isInStock && product.pricing.numericPrice && product.pricing.numericPrice >= 1000 && (
                  <Button 
                    onClick={() => setShowFinancingModal(true)} 
                    variant="outline"
                    className="w-full mt-3 border-2 border-green-600 text-green-700 hover:bg-green-50 py-5 text-base"
                    data-testid="financing-request-btn"
                  >
                    <Calculator className="mr-2" size={20} />
                    {t('products.financing')}
                  </Button>
                )}

                {/* PayPal-Ratenzahlung Option (nur für berechtigte Produkte ≤10k €) */}
                {isInStock && product.pricing.numericPrice && (
                  <PayPalInstallmentOption
                    product={{
                      slug: product.slug,
                      category: product.category,
                      name: product.name,
                      isAccessory: false,
                      stockQuantity: product.stockQuantity,
                    }}
                    netPrice={product.pricing.numericPrice}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* UNTERER BEREICH - Neue Reihenfolge */}
      {/* ============================================ */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* 1. PRODUKTBESCHREIBUNG - Ebene 1 Hauptüberschrift */}
          <div className="mb-10" data-testid="description-section">
            <h2 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
              {t('products.description')}
            </h2>
            <div className="bg-gray-50 rounded-b-xl p-6 border border-gray-200 border-t-0">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
                {getProductDescription()}
              </p>
            </div>
          </div>

          {/* 2. TECHNISCHE DATEN - Ebene 1 Hauptüberschrift (erweitert für Bagger) */}
          {isBagger && techDaten ? (
            <div className="mb-10" data-testid="technical-data-section">
              <h2 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
                {t('products.technicalData')}
              </h2>
              <div className="border border-gray-200 border-t-0 rounded-b-xl overflow-hidden">
              
                {/* Motor & Leistungsdaten - Ebene 2 Unterüberschrift */}
                <div className="border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-800 bg-gray-200 px-4 py-2">
                    {techDaten.motorLeistung.title}
                  </h3>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(techDaten.motorLeistung.data).map(([key, value], idx) => (
                        <tr key={key} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-6 py-3 font-medium text-gray-900 border-r border-gray-200 w-1/2">{key}</td>
                          <td className="px-6 py-3 text-gray-700">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Abmessungen & Gewicht - Ebene 2 Unterüberschrift */}
                {Object.keys(techDaten.abmessungenGewicht.data).length > 0 && (
                  <div className="border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800 bg-gray-200 px-4 py-2">
                      {techDaten.abmessungenGewicht.title}
                    </h3>
                    <table className="w-full">
                      <tbody>
                        {Object.entries(techDaten.abmessungenGewicht.data).map(([key, value], idx) => (
                          <tr key={key} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-3 font-medium text-gray-900 border-r border-gray-200 w-1/2">{key}</td>
                            <td className="px-6 py-3 text-gray-700">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Leistungsdaten - Ebene 2 Unterüberschrift */}
                {Object.keys(techDaten.leistungsdaten.data).length > 0 && (
                  <div className="border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800 bg-gray-200 px-4 py-2">
                      {techDaten.leistungsdaten.title}
                    </h3>
                    <table className="w-full">
                      <tbody>
                        {Object.entries(techDaten.leistungsdaten.data).map(([key, value], idx) => (
                          <tr key={key} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-3 font-medium text-gray-900 border-r border-gray-200 w-1/2">{key}</td>
                            <td className="px-6 py-3 text-gray-700">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Besonderheiten - Ebene 2 Unterüberschrift */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 bg-gray-200 px-4 py-2">
                    {techDaten.besonderheiten.title}
                  </h3>
                  <div className="bg-gray-50 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {techDaten.besonderheiten.data.map((item, idx) => (
                        <div key={idx} className="flex items-center bg-white p-3 rounded-lg border border-gray-200">
                          <CheckCircle className="text-green-600 mr-3 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
              <p className="text-sm text-gray-500 mt-4">Alle Angaben sind ca.-Werte. Technische Änderungen und Ausstattungsabweichungen vorbehalten.</p>
            </div>
          ) : product.technicalData && (
            <div className="mb-10" data-testid="technical-data-section">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Wrench className="text-red-600 mr-3" size={28} />
                Technische Daten
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-900">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white w-1/2">Eigenschaft</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white w-1/2">Wert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(product.technicalData).map(([key, value], idx) => (
                      <tr key={key} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-6 py-4 font-medium text-gray-900 border-r border-gray-200">{key}</td>
                        <td className="px-6 py-4 text-gray-700">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 mt-4">Alle Angaben sind ca.-Werte. Technische Änderungen und Ausstattungsabweichungen vorbehalten.</p>
            </div>
          )}

          {/* 3. SERIENAUSSTATTUNG (nur für Bagger) */}
          {isBagger && (
            <div className="mb-10" data-testid="serienausstattung-section">
              <h3 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
                {t('products.standardEquipment')}
              </h3>
              <div className="bg-gray-50 rounded-b-xl p-6 border border-gray-200 border-t-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {serienausstattungBagger.map((item, idx) => (
                    <div key={idx} className="flex items-center bg-white p-3 rounded-lg border border-gray-200">
                      <CheckCircle className="text-green-600 mr-3 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 4. LIEFERUMFANG (nur für Bagger) */}
          {isBagger && getLieferumfang() && (
            <div className="mb-10" data-testid="lieferumfang-section">
              <h3 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
                {t('products.deliveryScope')}
              </h3>
              <div className="bg-gray-50 rounded-b-xl p-6 border border-gray-200 border-t-0">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getLieferumfang().map((item, idx) => (
                    <li key={idx} className="flex items-center bg-white p-3 rounded-lg border border-gray-200">
                      <CheckCircle className="text-red-600 mr-3 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 5. CE-ZERTIFIKAT mit Download-Button */}
          <div className="mb-10" data-testid="certificates-section">
            <h3 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
              {t('products.ceCertified')}
            </h3>
            <div className="bg-gray-50 rounded-b-xl p-6 border border-gray-200 border-t-0">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">CE-Konformitätserklärung</span>
                </div>
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">EMV-Konformität (2014/30/EU)</span>
                </div>
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">Maschinenrichtlinie 2006/42/EG</span>
                </div>
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">Risikobeurteilung (EN ISO 12100)</span>
                </div>
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">Betriebsanleitung (DE/EN)</span>
                </div>
                <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <CheckCircle className="text-green-600 mr-2" size={20} />
                  <span className="text-green-800 font-semibold text-sm">Technische Dokumentation</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getCertificateUrl() ? (
                  <a 
                    href={getCertificateUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex"
                  >
                    <Button className="bg-green-600 hover:bg-green-700 text-white">
                      <Download className="mr-2" size={20} />
                      CE-Zertifikat herunterladen (PDF)
                    </Button>
                  </a>
                ) : (
                  <Button className="bg-green-600 hover:bg-green-700 text-white" disabled>
                    <Download className="mr-2" size={20} />
                    CE-Zertifikat herunterladen (PDF)
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* 6. VERSAND & LIEFERUNG */}
          <div className="mb-10" data-testid="shipping-section">
            <h3 className="text-lg font-semibold text-white bg-gray-900 px-4 py-2 rounded-t-lg">
              Versand & Lieferung
            </h3>
            <div className="bg-gray-50 rounded-b-xl p-6 border border-gray-200 border-t-0">
              {/* Einleitender Text */}
              <div className="prose prose-gray max-w-none mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Lieferung unserer Minibagger erfolgt in der Regel per Spedition im vollständig montierten und betriebsbereiten Zustand. Alle Maschinen werden werkseitig vormontiert ausgeliefert, sämtliche Betriebsstoffe (Öl, Kraftstoff, Hydrauliköl etc.) sind bereits eingefüllt, sodass das Gerät nach Erhalt sofort einsatzbereit ist.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Alternativ besteht selbstverständlich auch die Möglichkeit der <strong>Selbstabholung</strong> bei uns vor Ort. Gerne können Sie Ihren Minibagger mit einem geeigneten Anhänger oder Transportfahrzeug selbst abholen. Unser Team unterstützt Sie dabei auf Wunsch bei der Verladung.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Bei einer Anlieferung per Spedition stimmen wir die Transportart individuell mit Ihnen ab. Je nach Gegebenheiten vor Ort kann die Zustellung beispielsweise erfolgen:
                </p>
              </div>

              {/* Transportoptionen als Liste */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-red-600 mr-3 flex-shrink-0" size={18} />
                    <span className="text-gray-700">per LKW mit Hebebühne</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-red-600 mr-3 flex-shrink-0" size={18} />
                    <span className="text-gray-700">per LKW mit Kran</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-red-600 mr-3 flex-shrink-0" size={18} />
                    <span className="text-gray-700">oder auf einem geeigneten Anhänger</span>
                  </li>
                </ul>
              </div>

              <div className="prose prose-gray max-w-none mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die für Sie passende Lösung besprechen wir gerne im Vorfeld.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Vor dem ersten Einsatz empfehlen wir, alle Funktionen gemäß Bedienungsanleitung zu prüfen und eine kurze Sichtkontrolle der Maschine durchzuführen. Auf Wunsch erhalten Sie von uns eine <strong>persönliche Einweisung</strong> in die Bedienung und Wartung.
                </p>
              </div>

              {/* Versandkosten-Hinweis */}
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Versandkosten:</strong> Die Versandkosten ermitteln wir transparent und individuell nach Lieferadresse und Transportart. Für eine genaue Kalkulation oder weitere Fragen zur Lieferung und Einweisung steht Ihnen unser Kundenservice jederzeit gerne zur Verfügung.
                </p>
              </div>
            </div>
          </div>

          {/* 7. CTA Section - Kontaktbereich */}
          <div className="bg-gray-900 rounded-xl p-8 md:p-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Haben Sie Fragen zu diesem Produkt?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Unser Team berät Sie gerne persönlich und erstellt Ihnen ein individuelles Angebot.
            </p>
            <Link to="/kontakt">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8">
                Jetzt Kontakt aufnehmen
              </Button>
            </Link>
          </div>

          {/* 8. Zwei Videos am Ende */}
          <div className="grid grid-cols-2 gap-4 md:gap-6 mt-8">
            {/* Video 2 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>

            {/* Video 3 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video3-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

        </div>
      </section>

      {/* Financing Modal */}
      <FinancingModal 
        isOpen={showFinancingModal} 
        onClose={() => setShowFinancingModal(false)} 
        product={product} 
      />
      
      {/* Cart Added Modal */}
      <CartAddedModal 
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        product={product}
        quantity={1}
      />
      
      {/* Preorder Modal */}
      <PreorderModal 
        isOpen={showPreorderModal}
        onClose={() => setShowPreorderModal(false)}
        product={product}
      />
      
      {/* Mixed Cart Warning Modal */}
      <MixedCartWarningModal
        isOpen={showMixedCartWarning}
        onClose={() => setShowMixedCartWarning(false)}
        message={mixedCartMessage}
      />
    </div>
  );
};

export default ProductDetail;
